import React from 'react';
import { useIsMobile } from '../../../../shared/responsive/media-query';

export interface StepsContextProps {
    current: StepsEnum;
    previous: StepsEnum;
    toNext: () => void;
    toPrevious: () => void;
    activeIndex: () => number;
    shouldHideStepper: () => boolean;
    toStep: (step: StepsEnum) => void;
    isMobile: boolean;
    isCompleted: (step: StepsEnum) => boolean;
}

export enum StepsEnum {
    WELCOME = 'WELCOME',
    IDENTIFICATION = 'IDENTIFICATION',
    IDENTIFICATION_USER = 'IDENTIFICATION_USER',
    IDENTIFICATION_PERSON = 'IDENTIFICATION_PERSON',
    EDIT_EMAIL = 'EDIT_EMAIL',
    PASSWORD_AND_CONFIRM = 'PASSWORD_AND_CONFIRM',
    PASSWORD_ONLY = 'PASSWORD_ONLY',
    CONFIRM_ONLY = 'CONFIRM_ONLY',
    CONTACT = 'CONTACT',
    CONTACT_INFORMATIVE = 'CONTACT_INFORMATIVE',
    CONTACT_PHONE = 'CONTACT_PHONE',
    TERMS_OF_USE = 'TERMS_OF_USE'
}

export const StepsContext = React.createContext<StepsContextProps>({} as StepsContextProps);

interface StepsSequence {
    previous: StepsEnum;
    stepperIndex: number;
    next: StepsEnum;
}

const WEB_STEPS_SEQUENCE: Record<StepsEnum, StepsSequence> = {
    WELCOME: {
        stepperIndex: -1,
        previous: StepsEnum.WELCOME,
        next: StepsEnum.IDENTIFICATION
    },
    IDENTIFICATION: {
        stepperIndex: 0,
        previous: StepsEnum.WELCOME,
        next: StepsEnum.PASSWORD_AND_CONFIRM
    },
    IDENTIFICATION_USER: {
        stepperIndex: 0,
        previous: StepsEnum.WELCOME,
        next: StepsEnum.IDENTIFICATION
    },
    IDENTIFICATION_PERSON: {
        stepperIndex: 0,
        previous: StepsEnum.IDENTIFICATION,
        next: StepsEnum.PASSWORD_AND_CONFIRM
    },
    EDIT_EMAIL: {
        stepperIndex: 0,
        previous: StepsEnum.IDENTIFICATION,
        next: StepsEnum.IDENTIFICATION
    },
    PASSWORD_AND_CONFIRM: {
        stepperIndex: 1,
        previous: StepsEnum.IDENTIFICATION,
        next: StepsEnum.CONTACT
    },
    PASSWORD_ONLY: {
        stepperIndex: 1,
        previous: StepsEnum.IDENTIFICATION,
        next: StepsEnum.CONFIRM_ONLY
    },
    CONFIRM_ONLY: {
        stepperIndex: 1,
        previous: StepsEnum.PASSWORD_ONLY,
        next: StepsEnum.CONTACT
    },
    CONTACT: {
        stepperIndex: 2,
        previous: StepsEnum.PASSWORD_AND_CONFIRM,
        next: StepsEnum.TERMS_OF_USE
    },
    CONTACT_INFORMATIVE: {
        stepperIndex: 2,
        previous: StepsEnum.PASSWORD_AND_CONFIRM,
        next: StepsEnum.TERMS_OF_USE
    },
    CONTACT_PHONE: {
        stepperIndex: 2,
        previous: StepsEnum.PASSWORD_AND_CONFIRM,
        next: StepsEnum.TERMS_OF_USE
    },
    TERMS_OF_USE: {
        stepperIndex: 3,
        previous: StepsEnum.CONTACT,
        next: StepsEnum.TERMS_OF_USE
    }
};

const RESPONSIVE_STEPS_SEQUENCE: Record<StepsEnum, StepsSequence> = {
    WELCOME: {
        stepperIndex: -1,
        previous: StepsEnum.WELCOME,
        next: StepsEnum.IDENTIFICATION_USER
    },
    IDENTIFICATION: {
        stepperIndex: 0,
        previous: StepsEnum.WELCOME,
        next: StepsEnum.PASSWORD_ONLY
    },
    IDENTIFICATION_USER: {
        stepperIndex: 0,
        previous: StepsEnum.WELCOME,
        next: StepsEnum.IDENTIFICATION_PERSON
    },
    IDENTIFICATION_PERSON: {
        stepperIndex: 0,
        previous: StepsEnum.IDENTIFICATION_USER,
        next: StepsEnum.PASSWORD_ONLY
    },
    EDIT_EMAIL: {
        stepperIndex: 0,
        previous: StepsEnum.IDENTIFICATION_USER,
        next: StepsEnum.IDENTIFICATION_USER
    },
    PASSWORD_AND_CONFIRM: {
        stepperIndex: 1,
        previous: StepsEnum.IDENTIFICATION_PERSON,
        next: StepsEnum.CONTACT
    },
    PASSWORD_ONLY: {
        stepperIndex: 1,
        previous: StepsEnum.IDENTIFICATION_PERSON,
        next: StepsEnum.CONFIRM_ONLY
    },
    CONFIRM_ONLY: {
        stepperIndex: 1,
        previous: StepsEnum.PASSWORD_ONLY,
        next: StepsEnum.CONTACT_INFORMATIVE
    },
    CONTACT: {
        stepperIndex: 2,
        previous: StepsEnum.CONFIRM_ONLY,
        next: StepsEnum.TERMS_OF_USE
    },
    CONTACT_INFORMATIVE: {
        stepperIndex: 2,
        previous: StepsEnum.CONFIRM_ONLY,
        next: StepsEnum.CONTACT_PHONE
    },
    CONTACT_PHONE: {
        stepperIndex: 2,
        previous: StepsEnum.CONTACT_INFORMATIVE,
        next: StepsEnum.TERMS_OF_USE
    },
    TERMS_OF_USE: {
        stepperIndex: 3,
        previous: StepsEnum.CONTACT_INFORMATIVE,
        next: StepsEnum.TERMS_OF_USE
    }
};

export const StepsProvider = props => {
    const isMobile = useIsMobile();
    const [previous, setPrevious] = React.useState<StepsEnum>(StepsEnum.WELCOME);
    const [current, setCurrent] = React.useState<StepsEnum>(StepsEnum.WELCOME);

    const updateCurrent = (next: StepsEnum) => {
        setPrevious(current);
        setCurrent(next);
    };

    const currentSequence = () => (isMobile ? RESPONSIVE_STEPS_SEQUENCE : WEB_STEPS_SEQUENCE);

    const toNext = () => updateCurrent(currentSequence()[current].next);
    const toPrevious = () => updateCurrent(currentSequence()[current].previous);
    const activeIndex = () => currentSequence()[current].stepperIndex;
    const shouldHideStepper = () => current === StepsEnum.WELCOME;
    const toStep = (step: StepsEnum) => updateCurrent(step);

    const isCompleted = (step: StepsEnum) => currentSequence()[current].stepperIndex > currentSequence()[step].stepperIndex;

    React.useEffect(() => {
        if (isMobile) {
            if (current === StepsEnum.IDENTIFICATION) {
                setCurrent(StepsEnum.IDENTIFICATION_USER);
            } else if (current === StepsEnum.PASSWORD_AND_CONFIRM) {
                setCurrent(StepsEnum.PASSWORD_ONLY);
            } else if (current === StepsEnum.CONTACT) {
                setCurrent(StepsEnum.CONTACT_INFORMATIVE);
            }
        } else {
            if (current === StepsEnum.IDENTIFICATION_USER || current === StepsEnum.IDENTIFICATION_PERSON) {
                setCurrent(StepsEnum.IDENTIFICATION);
            } else if (current === StepsEnum.PASSWORD_ONLY || current === StepsEnum.CONFIRM_ONLY) {
                setCurrent(StepsEnum.PASSWORD_AND_CONFIRM);
            } else if (current === StepsEnum.CONTACT_INFORMATIVE || current === StepsEnum.CONTACT_PHONE) {
                setCurrent(StepsEnum.CONTACT);
            }
        }
    }, [isMobile, current]);

    const value: StepsContextProps = {
        current,
        previous,
        toNext,
        toPrevious,
        activeIndex,
        shouldHideStepper,
        toStep,
        isMobile,
        isCompleted
    };

    return <StepsContext.Provider value={value}>{props.children}</StepsContext.Provider>;
};

export const useSteps = () => React.useContext(StepsContext);

export const withStepsContext = () => <P extends object>(WrappedComponent: React.ComponentType<P>) => (props: P) => {
    return (
        <StepsProvider>
            <WrappedComponent {...props} />
        </StepsProvider>
    );
};
